// DirectoryFilterCard - Filter bar for FP Directory
// Adapted from admin FilterCard with two-tier collapsible design

import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Collapse
} from '@mui/material'
import { Search as SearchIcon, ExpandMore, ExpandLess } from '@mui/icons-material'
import { poolwerxColors } from '../../../theme/colors'
import { getMarketFilterOptions } from '../../../constants/markets'
import { apiClient } from '../../../services/api'
import type { DirectoryFilters } from '../hooks/useDirectoryFilters'

interface RegionOption {
  region_code: string
  region_name: string
  market: string
}

interface BdmOption {
  id: number
  name: string
  market: string
}

interface DirectoryFilterCardProps {
  filters: DirectoryFilters
  onFilterChange: (key: keyof DirectoryFilters, value: string) => void
  onClearFilters: () => void
  hasActiveFilters: boolean
  loading?: boolean
}

// Label style for filter fields
const labelStyle = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.75rem',
  mb: 0.5,
  display: 'block'
}

const DirectoryFilterCard: React.FC<DirectoryFilterCardProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
  loading = false
}) => {
  const marketOptions = getMarketFilterOptions()
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([])
  const [bdmOptions, setBdmOptions] = useState<BdmOption[]>([])
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [loadingBdms, setLoadingBdms] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Focus search input when component mounts and data is loaded
  useEffect(() => {
    if (searchInputRef.current && !loading) {
      searchInputRef.current.focus()
    }
  }, [loading])

  // Fetch regions when market changes
  useEffect(() => {
    const fetchRegions = async () => {
      if (!filters.market) {
        setRegionOptions([])
        return
      }
      setLoadingRegions(true)
      try {
        const response = await apiClient.get(`/admin/regions?market=${filters.market}&per_page=100`)
        setRegionOptions(response.data.regions || [])
      } catch (error) {
        console.error('Failed to fetch regions:', error)
        setRegionOptions([])
      } finally {
        setLoadingRegions(false)
      }
    }
    fetchRegions()
  }, [filters.market])

  // Fetch all BDMs on mount
  useEffect(() => {
    const fetchBdms = async () => {
      setLoadingBdms(true)
      try {
        const response = await apiClient.get('/admin/bdm-options')
        setBdmOptions(response.data || [])
      } catch (error) {
        console.error('Failed to fetch BDMs:', error)
        setBdmOptions([])
      } finally {
        setLoadingBdms(false)
      }
    }
    fetchBdms()
  }, [])

  // Clear dependent filters when market changes
  const handleMarketChange = (value: string) => {
    onFilterChange('market', value)
    if (filters.region) onFilterChange('region', '')
  }

  // Check if any secondary filters are active
  const hasSecondaryFilters =
    filters.bdm !== ''

  // Show secondary filters when expanded OR when any are active
  const showSecondaryFilters = expanded || hasSecondaryFilters

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        {/* Line 1: Search, FP Status, Market, Region */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography component="label" sx={labelStyle}>
              Search for Franchise Partners
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, code, or contact..."
              variant="outlined"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              disabled={loading}
              inputRef={searchInputRef}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: poolwerxColors.teal
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography component="label" sx={labelStyle}>
              FP Status
            </Typography>
            <FormControl fullWidth size="small" disabled={loading}>
              <Select
                displayEmpty
                value={filters.fpStatus}
                onChange={(e) => onFilterChange('fpStatus', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="Onboarding">Onboarding</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Terminated">Terminated</MenuItem>
                <MenuItem value="Test">Test</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography component="label" sx={labelStyle}>
              Market
            </Typography>
            <FormControl fullWidth size="small" disabled={loading}>
              <Select
                displayEmpty
                value={filters.market}
                onChange={(e) => handleMarketChange(e.target.value)}
              >
                <MenuItem value="">All Markets</MenuItem>
                {marketOptions.filter(opt => opt.value !== '').map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography component="label" sx={labelStyle}>
              Region
            </Typography>
            <FormControl fullWidth size="small" disabled={!filters.market || loadingRegions || loading}>
              <Select
                displayEmpty
                value={filters.region}
                onChange={(e) => onFilterChange('region', e.target.value)}
              >
                <MenuItem value="">All Regions</MenuItem>
                {regionOptions.map(option => (
                  <MenuItem key={option.region_code} value={option.region_code}>
                    {option.region_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Line 2: ROM/FBC, ICU Status, Selling Status (collapsible) */}
        <Collapse in={showSecondaryFilters}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <Typography component="label" sx={labelStyle}>
                ROM/FBC
              </Typography>
              <FormControl fullWidth size="small" disabled={loadingBdms || loading}>
                <Select
                  displayEmpty
                  value={filters.bdm}
                  onChange={(e) => onFilterChange('bdm', e.target.value)}
                >
                  <MenuItem value="">All ROM/FBC</MenuItem>
                  {bdmOptions.map(option => (
                    <MenuItem key={option.id} value={option.id.toString()}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>

        {/* Line 3: More/Less Filters and Clear Filters */}
        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="center">
          <Grid item xs={6}>
            {!hasSecondaryFilters && (
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
                sx={{ color: 'text.secondary' }}
              >
                {expanded ? 'Less Filters' : 'More Filters'}
              </Button>
            )}
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  onClearFilters()
                  setExpanded(false)
                }}
                disabled={!hasActiveFilters}
                sx={{
                  borderColor: poolwerxColors.red,
                  color: poolwerxColors.red,
                  '&:hover': {
                    borderColor: poolwerxColors.redHover || poolwerxColors.red,
                    backgroundColor: 'rgba(236, 28, 90, 0.04)',
                    color: poolwerxColors.redHover || poolwerxColors.red
                  },
                  '&:disabled': {
                    borderColor: 'action.disabled',
                    color: 'action.disabled'
                  }
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

export default DirectoryFilterCard
